import { Wallet, WalletPageConnection } from "@auer-steel/Services/AuerSteelApiModels";
import { deleteWalletById, getWalletPageConnection, getWalletsList } from "@auer-steel/Services/WalletService";
import { CreateWallet } from "@auer-steel/Widgets/Common/PayfabricWallet/CreateWallet";
import { UpdateWallet } from "@auer-steel/Widgets/Common/PayfabricWallet/UpdateWallet";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ServiceResult } from "@insite/client-framework/Services/ApiService";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Accordion, { AccordionProps } from "@insite/mobius/Accordion";
import AccordionSection, { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import Button from "@insite/mobius/Button";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal from "@insite/mobius/Modal";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

export interface WalletManagementStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    walletAccordion?: AccordionProps;
    walletAccordionSection?: AccordionSectionPresentationProps;
    createWalletSection?: InjectableCss;
}

export const walletStyles: WalletManagementStyles = {
    walletAccordionSection: {
        titleTypographyProps: {
            weight: "bold",
            css: css`
                text-transform: uppercase;
            `,
        },
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    createWalletSection: {
        css: css`
            margin: 5px 0;
            `,
    }
}

const styles = walletStyles;

const WalletManagement: React.FC<any> = () => {
    const [refreshCount, setRefreshCount] = React.useState(0);
    const [walletPageConnection, setWalletPageConnection] = React.useState<WalletPageConnection>();
    const [selectedWalletId, setSelectedWalletId] = React.useState("");
    const [wallets, setWallets] = React.useState<Wallet[]>([]);
    const [isLoading, setIsLoading] = React.useState(true);
    const [modalIsOpen, setModalIsOpen] = React.useState(false);
    const [createModalIsOpen, setCreateModalIsOpen] = React.useState(false);
    const toasterContext = React.useContext(ToasterContext);

    const onWalletCreateHandler = () => setRefreshCount(refreshCount + 1);

    const onWalletDeleteHandler = (paymentProfileId: string) => {
        toasterContext.addToast({ body: siteMessage("ACHAccount_DeleteSuccess"), messageType: "success" });
        setWallets(wallets.filter(r => r.PaymentProfileId !== paymentProfileId));
        setRefreshCount(refreshCount + 1);
        if (wallets.length !== 0) {
            setSelectedWalletId(wallets[0].PaymentProfileId);
        } else {
            setSelectedWalletId("");
        }
    };

    React.useEffect(() => {
        getWalletsList().then(res => {
            if (res.successful) {
                res.result = modifyMaskedCardNumber(res.result);
                setWallets(res.result);
                if (res.result.length > 0) {
                    setSelectedWalletId(res.result[0].PaymentProfileId);
                }
            }
            setIsLoading(false);
        })
            .catch(() => setIsLoading(false));
    }, [refreshCount]);

    const modalCloseHandler = () => {
        setModalIsOpen(false);   
        getWalletsList().then(res => {
            if (res.successful) {
                res.result = modifyMaskedCardNumber(res.result);
                setWallets(res.result);
                if (res.result.length > 0) {
                    setSelectedWalletId(res.result[0].PaymentProfileId);
                }
            }
            setIsLoading(false);
        })
            .catch(() => setIsLoading(false));    
    };

    const createModalCloseHandler = () => {
        setCreateModalIsOpen(false);     
        getWalletsList().then(res => {
            if (res.successful) {
                res.result = modifyMaskedCardNumber(res.result);
                setWallets(res.result);
                if (res.result.length > 0) {
                    setSelectedWalletId(res.result[0].PaymentProfileId);
                }
            }
            setIsLoading(false);
        })
            .catch(() => setIsLoading(false));     
    };

    const modalOpenHandler = () => {
        setModalIsOpen(true);       
    };

    const createModalOpenHandler = () => {
        setCreateModalIsOpen(true);    
        getWalletsList().then(res => {
            if (res.successful) {
                res.result = modifyMaskedCardNumber(res.result);
                setWallets(res.result);
                if (res.result.length > 0) {
                    setSelectedWalletId(res.result[0].PaymentProfileId);
                }
            }
            setIsLoading(false);
        })
            .catch(() => setIsLoading(false));   
    };

    const modifyMaskedCardNumber = (wallets: Wallet[]) =>{
        if (wallets && wallets.length > 0) {
            wallets.forEach(wallet => {
                wallet.MaskedCardNumber = `${wallet.Address1  } ${  wallet.MaskedCardNumber.substring(wallet.MaskedCardNumber.indexOf("..."))  } - ${  wallet.Address2}`;
            });
        }
        return wallets;
    }

    return (
        // <StyledWrapper {...styles.centeringWrapper}>
        <>
            <StyledWrapper {...styles.createWalletSection}>
                <Button onClick={createModalOpenHandler}>
                    {translate("Create ACH Account")}
                </Button>
                    <Modal
                    headline={translate("Create ACH Account")}
                    isOpen={createModalIsOpen}
                    handleClose={createModalCloseHandler}
                >
                    <CreateWallet modalCloseHandler={createModalCloseHandler}/>
                </Modal>
            </StyledWrapper>
            {isLoading && (
                <LoadingSpinner {...styles.spinner}></LoadingSpinner>
            )}
            <Accordion headingLevel={2}>
                {wallets && wallets.length > 0 && wallets.map(wallet => (
                    <AccordionSection
                        key={wallet.PaymentProfileId}
                        {...styles.walletAccordionSection}
                        title={wallet.MaskedCardNumber}
                        expanded={selectedWalletId === wallet.PaymentProfileId}
                        onTogglePanel={() => setSelectedWalletId(wallet.PaymentProfileId)}
                    >
                        {selectedWalletId && (
                            <div>
                                <Button onClick={modalOpenHandler} style={{marginRight: 10}}>
                                    {translate("Update Account")}
                                </Button>

                                <Button onClick={() => { deleteWalletById(wallet.PostalCode).then(() => onWalletDeleteHandler(wallet.PostalCode)); }}>
                                    {translate("Delete Account")}
                                </Button>

                                <Modal
                                    headline={translate("Update Account")}
                                    isOpen={modalIsOpen}
                                    handleClose={modalCloseHandler}
                                >
                                    <UpdateWallet wallet={wallet} modalCloseHandler={modalCloseHandler}/>
                                </Modal>
                            </div>
                        )}
                    </AccordionSection>
                ))}
                {wallets && wallets.length <= 0 && !isLoading && (
                    <AccordionSection
                        key="0"
                        {...styles.walletAccordionSection}
                        title="No accounts saved"
                        expanded={false}
                        onTogglePanel={() => false}>
                    </AccordionSection>
                )}
            </Accordion>
        </>
    );
};

const definition: WidgetDefinition = {
    group: "PayfabricWallet" as any,
};

const widgetModule: WidgetModule = {
    component: WalletManagement,
    definition,
};

export default widgetModule;
